import {PortalUserMin, UserId} from "platformed-client/types";
import {Badge, Icon, IconButton, Text} from "@chakra-ui/react";
import {ClockIcon, TrashIcon} from "@heroicons/react/16/solid";
import {createColumn, createTableConfig} from "../../../../hooks/table";
import {createPresetDateTimeColumn} from "../../../../hooks/table/common/dateTime";
import {compareOptional} from "../../../../utils/compare";
import T from "platformed-components/i18n/T";
import {extractEmail, extractUserId} from "./portalUser";

type PortalUsersTableContext = {onDelete: (userId: UserId) => void} | null;

const PORTAL_USERS_TABLE_CONFIG = createTableConfig<PortalUserMin, PortalUsersTableContext>()({
  tableUniqueKey: "tprmThirdPartyPortalUsers",
  extractUniqueKey: extractUserId,
  columns: {
    email: createColumn({
      label: <T keyName="c.Email" />,
      extract: extractEmail,
      compare: (a, b) => a.localeCompare(b),
      render: email => email,
      fractionalWidth: 2,
    }),
    name: createColumn({
      label: <T keyName="c.Name" />,
      extract: pu => {
        if (pu.info.type === "InvitePending") {
          return null;
        } else {
          return pu.info.content.user.name;
        }
      },
      compare: (a, b) => compareOptional(a, b, (a, b) => a.localeCompare(b)),
      render: name => <Text fontWeight="medium">{name ?? "-"}</Text>,
      forceVisible: true,
      fractionalWidth: 2,
    }),
    lastVisited: createPresetDateTimeColumn({
      label: <T keyName="c.Last_visited" />,
      extract: pu => {
        if (pu.info.type === "InvitePending") {
          return null;
        } else {
          return pu.info.content.last_visited_at;
        }
      },
      placeholder: (
        <Badge colorPalette="orange">
          <Icon asChild boxSize="1em">
            <ClockIcon />
          </Icon>
          <T keyName="c.Invite_pending" />
        </Badge>
      ),
    }),
    actions: createColumn({
      label: <T keyName="c.Actions" />,
      extract: extractUserId,
      render: (userId, _pu, context) => {
        return (
          <IconButton
            size="xs"
            variant="ghost"
            colorPalette="red"
            onClick={() => context!.onDelete(userId)}
            aria-label="Delete user"
          >
            <TrashIcon />
          </IconButton>
        );
      },
      hide: ctx => ctx === null,
      forceVisible: true,
      fractionalWidth: 1,
    }),
  },
});

export default PORTAL_USERS_TABLE_CONFIG;
